import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Input,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { ProviderApi } from 'services';
import { toastMessages } from 'config/constants';
import { useTheme } from '@material-ui/core/styles';
import DataTable from 'components/DataTable';
import DataTableSearchToolbar from 'components/DataTableSearchToolbar';
import moment from 'moment';
import momentFR from 'moment/min/locales';
import toast from 'react-hot-toast';

moment.locale('fr', momentFR);
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 0;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(name, personName, theme) {
  return {
    fontWeight:
      personName.indexOf(name) === -1 ? theme.typography.fontWeightRegular : theme.typography.fontWeightMedium,
  };
}

const genders = ['Masculin', 'Féminin', 'Non communiqué'];
const products = ['12A1', '13B2', '14B3'];

const headCells = [
  { id: 'clientID', label: 'ID Client', sortable: true },
  { id: 'clientLastname', label: 'Nom', sortable: true },
  { id: 'clientFirstname', label: 'Prénom', sortable: true },
  { id: 'clientEmail', label: 'Email', sortable: false },
  { id: 'activity', label: 'Activité', sortable: false },
  { id: 'startDate', label: 'Date', sortable: true },
  { id: 'duration', label: 'Durée', sortable: true },
  { id: 'gender', label: 'Genre', sortable: false },
  { id: 'age', label: 'Age', sortable: true },
  { id: 'productID', label: 'ID Produit', sortable: true },
  { id: 'price', label: 'Prix', sortable: true },
  { id: 'number', label: 'Nombre', sortable: true },
];

const Clients = () => {
  const theme = useTheme();

  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [clientLastname, setClientLastname] = useState('');
  const [clientFirstname, setClientFirstname] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [date, setDate] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState(0);
  const [department, setDepartment] = useState('');
  const [city, setCity] = useState('');
  const [productID, setProductID] = useState('');
  const [price, setPrice] = useState(0);
  const [number, setNumber] = useState(0);

  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const getClientsData = useCallback(() => {
    setIsLoading(true);
    ProviderApi.getClients()
      .then(({ data }) => {
        const clients = data.map((c) => {
          const client = c.client;
          const service = c.service;

          return {
            clientID: client.id,
            clientLastname: client.last_name,
            clientFirstname: client.first_name,
            clientEmail: client.email,
            startDate: moment(c.start_date).format('dddd Do MMMM YYYY à HH:mm'),
            endDate: c.end_date,
            duration: service.duration,
            activity: service.name,
            gender: client.gender ?? null,
            age: client.birthdate ? moment().diff(moment(client.birthdate), 'years') : null,
            productID: service.identifier,
            price: c.price,
            number: c.quantity,
          };
        });

        setClients(clients);
        setFilteredClients(formatClientData(clients));
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        toast.error(toastMessages.error.FETCH_CLIENTS);
      });
  }, []);

  useEffect(() => {
    getClientsData();
  }, [getClientsData]);

  const formatClientData = (data) => {
    return data.map((value) => ({
      ...value,
      gender: value.gender ? (value.gender === 'female' ? 'Femme' : 'Homme') : 'non renseigné',
      duration: value.duration + ' min',
      age: value.age ? value.age + ' ans' : 'non renseigné',
      price: value.price + ' €',
    }));
  };

  const refreshFilteredClient = (searchValue) => {
    setFilteredClients(
      formatClientData(
        clients.filter((client) => {
          return client.clientLastname.includes(searchValue) || client.clientFirstname.includes(searchValue);
        }),
      ),
    );
  };

  const handleSave = (event) => {
    event.preventDefault();
    handleClose(event);
  };

  const handleClose = (event) => {
    event.preventDefault();
    setClientLastname('');
    setClientFirstname('');
    setClientEmail('');
    setDate('');
    setGender('');
    setAge(0);
    setDepartment('');
    setCity('');
    setProductID('');
    setPrice(0);
    setNumber(0);
    setAnchorEl(null);
  };

  const handleSelectChange = (event) => {
    if (event.target.name === 'productID') setProductID(event.target.value);
    if (event.target.name === 'gender') setGender(event.target.value);
  };

  return (
    <>
      <Grid container>
        <Grid container item xs={12} justifyContent="center">
          <Typography variant="h1">Clients</Typography>
        </Grid>

        <div style={{ marginTop: '16px', width: '100%' }}>
          <DataTable
            headCells={headCells}
            data={filteredClients}
            loading={isLoading}
            ToolbarComponent={DataTableSearchToolbar}
            toolbarProps={{
              onSearchChange: (event) => {
                refreshFilteredClient(event.target.value ?? '');
              },
            }}
          />
        </div>

        <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
          <DialogTitle id="form-dialog-title">Subscribe</DialogTitle>
          <DialogContent>
            <DialogContentText>Renseignez les détails du client.</DialogContentText>
            <TextField
              name="clientLastname"
              autoFocus
              margin="dense"
              id="clientLastname"
              label="Nom du client"
              value={clientLastname}
              onChange={(e) => setClientLastname(e.target.value)}
              fullWidth
            />
            <TextField
              autoFocus
              name="clientFirstname"
              margin="dense"
              id="clientFirstname"
              label="Prénom du client"
              value={clientFirstname}
              onChange={(e) => setClientFirstname(e.target.value)}
              fullWidth
            />
            <TextField
              autoFocus
              margin="dense"
              name="clientEmail"
              id="clientEmail"
              label="Email"
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              fullWidth
            />
            <TextField
              autoFocus
              margin="dense"
              name="date"
              id="date"
              label="Date"
              type="datetime-local"
              InputLabelProps={{
                shrink: true,
              }}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              fullWidth
            />
            <InputLabel id="demo-mutiple-chip-label">Sexe</InputLabel>
            <Select
              labelId="demo-mutiple-chip-label"
              id="gender"
              name="gender"
              value={gender}
              onChange={handleSelectChange}
              input={<Input id="BootstrapInput" />}
              MenuProps={MenuProps}
              fullWidth
            >
              {genders.map((genderOption) => (
                <MenuItem key={genderOption} value={genderOption} style={getStyles(genderOption, gender, theme)}>
                  {genderOption}
                </MenuItem>
              ))}
            </Select>
            <TextField
              autoFocus
              margin="dense"
              name="age"
              id="age"
              label="Age"
              type="number"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              fullWidth
            />
            <TextField
              autoFocus
              margin="dense"
              name="department"
              id="department"
              label="Département"
              type="number"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              fullWidth
            />
            <TextField
              autoFocus
              margin="dense"
              name="city"
              id="city"
              label="Ville"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              fullWidth
            />
            <InputLabel id="demo-mutiple-chip-label">ID Produit</InputLabel>
            <Select
              labelId="demo-mutiple-chip-label"
              id="productID"
              name="productID"
              value={productID}
              onChange={handleSelectChange}
              input={<Input id="BootstrapInput" />}
              MenuProps={MenuProps}
              fullWidth
            >
              {products.map((product) => (
                <MenuItem key={product} value={product} style={getStyles(product, productID, theme)}>
                  {product}
                </MenuItem>
              ))}
            </Select>
            <TextField
              autoFocus
              margin="dense"
              name="price"
              id="price"
              label="Prix"
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              fullWidth
            />
            <TextField
              autoFocus
              margin="dense"
              name="number"
              id="number"
              label="Nombre"
              type="number"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} name="cancel" color="primary">
              Annuler
            </Button>
            <Button onClick={handleSave} name="save" color="primary">
              Sauvegarder
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </>
  );
};

export default Clients;
